/**
 * Segmentação de clientes como REGRA AVALIADA, não como lista materializada
 * (Task 13, brief 3.4 / D-13.5).
 *
 * Por que assim: uma tabela de "membros do segmento" envelhece em silêncio — o
 * cliente marcado como "inativo 90 dias" continuaria marcado no dia em que
 * voltou a comprar. Aqui o segmento é uma regra salva (`crm_segments.rule`,
 * migration 0032) avaliada sobre os registros reais (pedidos unificados,
 * conversas, orçamentos, estágio do funil) a cada leitura. O preço é recalcular
 * na leitura; a troca é o segmento nunca discordar dos registros.
 *
 * Invariante de honestidade (D-13.7): avaliar pode dar **três** respostas, não
 * duas — `match`, `no_match` e `unknown`. `unknown` existe para quando a regra
 * depende de um fato que a fonte não publicou neste ambiente (ex.: pedidos
 * unificados `awaiting_contract`): dizer "não pertence" nessa situação faria o
 * operador concluir que o cliente não compra, quando o que houve foi fonte
 * ausente. A tela mostra os três estados separadamente.
 *
 * Puro e sem I/O (ADR-0007, mesmo regime do pipeline).
 */
import { isLeadStage, isOpportunitySource, type LeadStage, type OpportunitySource } from "./pipeline.js";

/**
 * Fatos do cliente usados pelas regras. Cada campo `null` significa "a fonte
 * desse fato não está disponível" — e não zero. É o que permite devolver
 * `unknown` em vez de uma resposta inventada.
 */
export interface CustomerSegmentFacts {
  /** Pedidos pagos (ou adiante na máquina) do cliente; `null` = fonte ausente. */
  paidOrderCount: number | null;
  /** Soma dos pedidos pagos em centavos; `null` = fonte ausente. */
  totalSpentCents: number | null;
  /** ISO da última compra; `null` = sem compra conhecida OU fonte ausente. */
  lastPurchaseAt: string | null;
  /** `true` quando a fonte de pedidos respondeu (mesmo sem nenhum pedido). */
  ordersAvailable: boolean;
  /** ISO da última interação de atendimento; `null` = sem conversa conhecida. */
  lastInteractionAt: string | null;
  /** Estágios das oportunidades do cliente (vazio = sem oportunidade). */
  stages: LeadStage[];
  /** `true` quando o cliente tem proposta em aberto (rascunho/enviada). */
  hasOpenQuote: boolean;
  /** Canais em que o cliente aparece (origem de oportunidades/pedidos). */
  channels: OpportunitySource[];
  /** Empresa vinculada (B2B); `null` = pessoa física. */
  companyId: string | null;
}

/**
 * Regra declarativa do segmento. O formato é intencionalmente pequeno: cada
 * variante corresponde a uma pergunta que a operação faz hoje. Regra nova entra
 * aqui **e** no teste — o `jsonb` da 0032 aceita evolução sem migration, mas
 * não aceita regra que ninguém sabe interpretar (ver `assertSegmentRule`).
 */
export type SegmentRule =
  | { kind: "paid_orders_min"; value: number }
  | { kind: "total_spent_min_cents"; value: number }
  | { kind: "no_purchase_for_days"; value: number }
  | { kind: "stage_in"; stages: LeadStage[] }
  | { kind: "has_open_quote" }
  | { kind: "channel_in"; channels: OpportunitySource[] }
  | { kind: "is_company" }
  | { kind: "all"; rules: SegmentRule[] };

export type SegmentMatch =
  | { state: "match" }
  | { state: "no_match" }
  /** A regra não pôde ser avaliada: diga por quê, nunca responda "não". */
  | { state: "unknown"; reason: string };

const MATCH: SegmentMatch = { state: "match" };
const NO_MATCH: SegmentMatch = { state: "no_match" };

function decide(value: boolean): SegmentMatch {
  return value ? MATCH : NO_MATCH;
}

/**
 * Validação de borda da regra lida do banco (`crm_segments.rule` é `jsonb`).
 * Regra desconhecida **não** vira "não pertence": ela é recusada aqui e a tela
 * declara o segmento como não avaliável, para ninguém operar sobre uma
 * segmentação que o código não entende.
 */
export function parseSegmentRule(value: unknown): SegmentRule | null {
  if (typeof value !== "object" || value === null) return null;
  const rule = value as Record<string, unknown>;
  switch (rule.kind) {
    case "paid_orders_min":
    case "total_spent_min_cents":
    case "no_purchase_for_days":
      return typeof rule.value === "number" && Number.isFinite(rule.value)
        ? ({ kind: rule.kind, value: rule.value } as SegmentRule)
        : null;
    case "stage_in": {
      if (!Array.isArray(rule.stages) || !rule.stages.length) return null;
      if (!rule.stages.every(isLeadStage)) return null;
      return { kind: "stage_in", stages: rule.stages as LeadStage[] };
    }
    case "channel_in": {
      if (!Array.isArray(rule.channels) || !rule.channels.length) return null;
      if (!rule.channels.every(isOpportunitySource)) return null;
      return { kind: "channel_in", channels: rule.channels as OpportunitySource[] };
    }
    case "has_open_quote":
      return { kind: "has_open_quote" };
    case "is_company":
      return { kind: "is_company" };
    case "all": {
      if (!Array.isArray(rule.rules) || !rule.rules.length) return null;
      const parsed = rule.rules.map(parseSegmentRule);
      if (parsed.some((item) => item === null)) return null;
      return { kind: "all", rules: parsed as SegmentRule[] };
    }
    default:
      return null;
  }
}

/** Dias desde a data informada; `null` quando não há data comparável. */
function daysSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.floor((now.getTime() - at) / 86_400_000);
}

/**
 * Avalia uma regra contra os fatos do cliente.
 *
 * As regras que dependem de pedido devolvem `unknown` quando a fonte de pedidos
 * não respondeu (`ordersAvailable: false`): é a diferença entre "este cliente
 * não comprou" e "não sei se comprou".
 */
export function evaluateSegmentRule(rule: SegmentRule, facts: CustomerSegmentFacts, now: Date): SegmentMatch {
  switch (rule.kind) {
    case "paid_orders_min":
      if (!facts.ordersAvailable || facts.paidOrderCount === null) {
        return { state: "unknown", reason: "fonte de pedidos não disponível neste ambiente" };
      }
      return decide(facts.paidOrderCount >= rule.value);
    case "total_spent_min_cents":
      if (!facts.ordersAvailable || facts.totalSpentCents === null) {
        return { state: "unknown", reason: "fonte de pedidos não disponível neste ambiente" };
      }
      return decide(facts.totalSpentCents >= rule.value);
    case "no_purchase_for_days": {
      if (!facts.ordersAvailable) {
        return { state: "unknown", reason: "fonte de pedidos não disponível neste ambiente" };
      }
      const days = daysSince(facts.lastPurchaseAt, now);
      // Nunca comprou: a fonte respondeu e não há compra. O cliente ESTÁ sem
      // comprar há mais tempo que qualquer janela, então a regra casa — e isso
      // é diferente de `unknown` acima.
      if (days === null) return MATCH;
      return decide(days >= rule.value);
    }
    case "stage_in":
      return decide(facts.stages.some((stage) => rule.stages.includes(stage)));
    case "has_open_quote":
      return decide(facts.hasOpenQuote);
    case "channel_in":
      return decide(facts.channels.some((channel) => rule.channels.includes(channel)));
    case "is_company":
      return decide(facts.companyId !== null);
    case "all": {
      const results = rule.rules.map((inner) => evaluateSegmentRule(inner, facts, now));
      // `no_match` é definitivo (o E lógico já é falso); só então `unknown`
      // domina — assim uma fonte ausente não apaga uma reprovação certa.
      const refused = results.find((result) => result.state === "no_match");
      if (refused) return refused;
      const unknown = results.find((result) => result.state === "unknown");
      if (unknown) return unknown;
      return MATCH;
    }
  }
}

export interface SegmentLike {
  key: string;
  name: string;
  active: boolean;
  rule: unknown;
}

export interface SegmentEvaluation {
  key: string;
  name: string;
  match: SegmentMatch;
  /** `false` quando a regra salva não é interpretável por este código. */
  ruleUnderstood: boolean;
}

/**
 * Avalia todos os segmentos ativos para um cliente. Segmento inativo não é
 * avaliado (a regra foi desligada de propósito) e segmento com regra
 * desconhecida entra como `unknown` declarado, não como "não pertence".
 */
export function evaluateSegments(
  segments: SegmentLike[],
  facts: CustomerSegmentFacts,
  now: Date
): SegmentEvaluation[] {
  return segments
    .filter((segment) => segment.active)
    .map((segment) => {
      const rule = parseSegmentRule(segment.rule);
      if (!rule) {
        return {
          key: segment.key,
          name: segment.name,
          ruleUnderstood: false,
          match: { state: "unknown", reason: "regra salva não reconhecida por esta versão da Plataforma" } as SegmentMatch
        };
      }
      return { key: segment.key, name: segment.name, ruleUnderstood: true, match: evaluateSegmentRule(rule, facts, now) };
    });
}

/** Segmentos a que o cliente pertence — a lista que vira `tags` no 360. */
export function matchedSegmentKeys(evaluations: SegmentEvaluation[]): string[] {
  return evaluations.filter((evaluation) => evaluation.match.state === "match").map((evaluation) => evaluation.key);
}
